import React, { useState, useEffect, useCallback } from 'react';
import {
  ChatBubbleLeftRightIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { apiClient } from '../../services/api';
import toast from 'react-hot-toast';

const HumanSupport = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    page: 1,
    limit: 20
  });
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Scroll to bottom when messages change in admin panel
  useEffect(() => {
    if (selectedConversation && messages.length > 0) {
      setTimeout(() => {
        const messagesContainer = document.querySelector('.admin-messages-container');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 100);
    }
  }, [messages, selectedConversation]);

  // Auto-refresh messages when viewing a conversation
  useEffect(() => {
    if (!selectedConversation || !autoRefresh) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.get(`/support-chat/admin/conversations/${selectedConversation.id}`);
        const newMessages = response.data.messages || [];

        // Only update if there are new messages
        if (newMessages.length !== messages.length) {
          setMessages(newMessages);
        }
      } catch (error) {
        console.error('Failed to poll conversation messages:', error);
      }
    }, 3000); // Poll every 3 seconds for admin

    return () => clearInterval(pollInterval);
  }, [selectedConversation, autoRefresh, messages.length]);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/support-chat/admin/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      params.append('page', filters.page);
      params.append('limit', filters.limit);

      const response = await apiClient.get(`/support-chat/admin/conversations?${params}`);
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/support-chat/admin/agents');
      setAgents(response.data.agents);
    } catch (error) {
      console.error('Failed to load agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadStats();
    } else if (activeTab === 'conversations') {
      loadConversations();
    } else if (activeTab === 'agents') {
      loadAgents();
    }
  }, [activeTab, loadAgents, loadConversations, loadStats]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const interval = setInterval(() => {
      if (activeTab === 'dashboard') {
        loadStats();
      } else if (activeTab === 'conversations') {
        loadConversations();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [activeTab, autoRefresh, loadConversations, loadStats]);

  const loadConversationDetails = async (conversationId) => {
    try {
      const response = await apiClient.get(`/support-chat/admin/conversations/${conversationId}`);
      setSelectedConversation(response.data.conversation);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to load conversation details:', error);
      toast.error('Failed to load conversation details');
    }
  };

  const assignConversation = async (conversationId, agentId = null) => {
    try {
      const payload = agentId ? { agentId } : {};
      await apiClient.post(`/support-chat/admin/conversations/${conversationId}/assign`, payload);
      toast.success('Conversation assigned successfully');
      loadConversations();
      if (selectedConversation?.id === conversationId) {
        loadConversationDetails(conversationId);
      }
    } catch (error) {
      console.error('Failed to assign conversation:', error);
      toast.error('Failed to assign conversation');
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;

    try {
      await apiClient.post(`/support-chat/admin/conversations/${selectedConversation.id}/reply`, {
        message: replyMessage.trim()
      });

      setReplyMessage('');
      toast.success('Reply sent successfully');

      // Reload conversation details and maintain scroll position
      await loadConversationDetails(selectedConversation.id);
      loadConversations();

      // Scroll to bottom after a short delay to ensure messages are rendered
      setTimeout(() => {
        const messagesContainer = document.querySelector('.admin-messages-container');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const closeConversation = async (conversationId) => {
    try {
      await apiClient.post(`/support-chat/admin/conversations/${conversationId}/close`);
      toast.success('Conversation closed successfully');
      loadConversations();
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to close conversation:', error);
      toast.error('Failed to close conversation');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-blue-100 text-blue-800',
      waiting_human: 'bg-yellow-100 text-yellow-800',
      human_assigned: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      active: 'Active',
      waiting_human: 'Waiting for Human',
      human_assigned: 'Assigned',
      closed: 'Closed'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[status] || badges.active}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getSenderIcon = (senderType) => {
    switch (senderType) {
      case 'user':
        return <UserIcon className="h-4 w-4" />;
      case 'ai':
        return <ChatBubbleLeftRightIcon className="h-4 w-4" />;
      case 'human_agent':
        return <UserIcon className="h-4 w-4 text-green-600" />;
      default:
        return <ChatBubbleLeftRightIcon className="h-4 w-4" />;
    }
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Conversations</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.total_conversations || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending Human Support</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.pending_human_support || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Assigned Conversations</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.assigned_conversations || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Closed Conversations</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.closed_conversations || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              setActiveTab('conversations');
              setFilters({ ...filters, status: 'waiting_human' });
            }}
            className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
          >
            <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
            View Pending Requests ({stats.pending_human_support || 0})
          </button>
          
          <button
            onClick={() => {
              setActiveTab('conversations');
              setFilters({ ...filters, status: 'human_assigned' });
            }}
            className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            <UserIcon className="h-4 w-4 mr-2" />
            View Assigned ({stats.assigned_conversations || 0})
          </button>
          
          <button
            onClick={() => setActiveTab('agents')}
            className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <UserGroupIcon className="h-4 w-4 mr-2" />
            Manage Agents
          </button>
        </div>
      </div>
    </div>
  );

  const renderConversations = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Status Filter</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="waiting_human">Waiting for Human</option>
              <option value="human_assigned">Assigned</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {conversations.map((conversation) => (
            <li key={conversation.id}>
              <div className="px-4 py-4 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                <div className="flex items-center space-x-4 min-w-0">
                  <div className="flex-shrink-0">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {conversation.user_name}
                      </p>
                      <div className="flex-shrink-0">
                      {getStatusBadge(conversation.status)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 truncate max-w-full" title={conversation.last_message || 'No messages yet'}>
                      {conversation.last_message || 'No messages yet'}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                      <span>{conversation.message_count} messages</span>
                      <span>{formatTime(conversation.updated_at)}</span>
                      {conversation.agent_name && (
                        <span>Assigned to: {conversation.agent_name}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-shrink-0 flex-wrap items-center gap-2 lg:justify-end">
                  <button
                    onClick={() => loadConversationDetails(conversation.id)}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    View
                  </button>
                  {conversation.status === 'waiting_human' && (
                    <button
                      onClick={() => assignConversation(conversation.id)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                    >
                      Assign to Me
                    </button>
                  )}
                  {conversation.status !== 'closed' && (
                    <button
                      onClick={() => closeConversation(conversation.id)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Conversation Details Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Conversation with {selectedConversation.user_name}
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedConversation(null);
                      setMessages([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Messages */}
                <div className="admin-messages-container max-h-96 overflow-y-auto border rounded-lg p-4 space-y-4 mb-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg ${
                          message.sender_type === 'user'
                            ? 'bg-blue-600 text-white'
                            : message.is_system_message
                            ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                            : message.sender_type === 'human_agent'
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          {getSenderIcon(message.sender_type)}
                          <span className="text-xs font-medium">
                            {message.sender_type === 'user' ? 'User' :
                             message.sender_type === 'human_agent' ? 'Agent' : 'AI'}
                          </span>
                          <span className="text-xs opacity-75">
                            {formatTime(message.created_at)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{message.message_text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                {selectedConversation.status !== 'closed' && (
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => e.key === 'Enter' && sendReply()}
                    />
                    <button
                      onClick={sendReply}
                      disabled={!replyMessage.trim()}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition-colors flex items-center space-x-2"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      <span>Send</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAgents = () => (
    <div className="space-y-6">
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Available Support Agents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent) => (
              <div key={agent.id} className="border rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <UserIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{agent.business_name}</p>
                    <p className="text-sm text-gray-500">{agent.email}</p>
                    <p className="text-xs text-gray-400">
                      {agent.active_conversations} active conversations
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Human Support Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage customer support conversations and assign them to human agents
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <button
            onClick={() => {
              if (activeTab === 'dashboard') loadStats();
              else if (activeTab === 'conversations') loadConversations();
              else if (activeTab === 'agents') loadAgents();
            }}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Auto-refresh</span>
          </label>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'dashboard', name: 'Dashboard', icon: ChartBarIcon },
            { id: 'conversations', name: 'Conversations', icon: ChatBubbleLeftRightIcon },
            { id: 'agents', name: 'Agents', icon: UserGroupIcon }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'conversations' && renderConversations()}
          {activeTab === 'agents' && renderAgents()}
        </>
      )}
    </div>
  );
};

export default HumanSupport;
